import type { ItemFiltersType } from '../api/types/itemFilters';
import type { ItemRequestModelField } from '../api/types/items';

export const commonQueryKeys = {
  loops: {
    all: (orgId: string) => ['loops', orgId] as const,
    // Distinct from `all`: that key holds one page, this one holds every page paged together.
    everyPage: (orgId: string) => [...commonQueryKeys.loops.all(orgId), 'everyPage'] as const,
    detail: ({ loopId }: { loopId: number }) => ['loops', 'detail', loopId],
  },
  customFields: {
    all: () => ['customFields'],
    search: (search: string) => ['customFields', search],
    details: (id: number) => [...commonQueryKeys.customFields.all(), 'details', id],
    picklistOptions: (customFieldId: number) => [
      ...commonQueryKeys.customFields.details(customFieldId),
      'picklistOptions',
      customFieldId,
    ],
  },
  estimationSchemes: {
    all: () => ['estimationSchemes'],
    details: (id: number) => [...commonQueryKeys.estimationSchemes.all(), 'details', id],
  },
  itemTypes: {
    all: ['itemTypes'],
    deletable: ['itemTypes', 'deletable'],
    delete: ['itemTypes', 'delete'],
    fields: (itemTypeId: number) => ['itemTypes', itemTypeId, 'fields'],
  },
  itemTypeLevels: {
    all: ['itemTypeLevels'],
    create: ['itemTypeLevels', 'create'],
    delete: ['itemTypeLevels', 'delete'],
    details: (levelId: number) => ['itemTypeLevels', 'details', levelId],
  },
  items: {
    all: () => ['items'] as const,
    byFilters: ({
      filter,
      limit,
      order,
      hierarchical,
    }: {
      filter?: ItemFiltersType;
      limit?: number;
      order?: string;
      hierarchical?: boolean;
    } = {}) => ['items', { filter, limit, order, hierarchical }],
  },
  itemsDataSet: {
    all: () => ['itemsDataSet'] as const,
    byFilter: (filter?: ItemFiltersType) => ['itemsDataSet', filter],
  },
  itemsTreeSet: {
    all: () => ['itemsTreeSet'] as const,
    byFilter: (filter?: ItemFiltersType) => ['itemsTreeSet', filter],
  },
  insightsItemsDataSet: (filters?: ItemFiltersType) =>
    filters && Object.keys(filters).length > 0
      ? ['insightsItemsDataSet', { filters }]
      : ['insightsItemsDataSet'],
  itemsBulkFetch: {
    all: () => ['itemsBulkFetch'] as const,
    byParams: ({
      ids,
      fields,
      filter,
    }: {
      ids: number[];
      fields?: ItemRequestModelField[];
      filter?: ItemFiltersType;
    }) => [
      'itemsBulkFetch',
      {
        ids,
        ...(fields ? { fields } : {}),
        ...(filter ? { filter } : {}),
      },
    ],
  },
  itemsBulkFetchV2: {
    all: () => ['itemsBulkFetchV2'] as const,
    byParams: ({
      ids,
      customFieldIds,
      fieldIds,
    }: {
      ids: number[];
      customFieldIds?: number[];
      fieldIds?: string[];
    }) => ['itemsBulkFetchV2', { ids, customFieldIds, fieldIds }],
    // Rooted at the same `all()` as `byParams` so an item-mutation invalidation of `all()` also
    // catches LoopFinancialsTable's per-page top-level cache (useLoopFinancialsPagedQuery's tier
    // 1), not just the eager `byParams` query. Tier 1 only — that hook's descendant tier lives
    // outside the query client and has no equivalent refresh path yet.
    paged: (datasetSignature: string, pageIndex: number) => [
      ...commonQueryKeys.itemsBulkFetchV2.all(),
      'paged',
      datasetSignature,
      pageIndex,
    ],
  },
  plannerTimelineItems: {
    all: () => ['plannerTimelineItems'] as const,
    byLoop: ({
      loopId,
      pageCount,
      customFieldIds,
    }: {
      loopId: number;
      pageCount: number;
      customFieldIds: number[];
    }) => ['plannerTimelineItems', { loopId, pageCount, customFieldIds }] as const,
  },
  item: (itemId: number) => ['item', itemId],
  itemAll: () => ['item'] as const,
  itemAncestors: (itemId: number) => ['itemAncestors', itemId],
  itemDescendants: {
    all: () => ['itemDescendants'] as const,
    byItemIds: (itemIds: number[]) => ['itemDescendants', [...itemIds].sort((a, b) => a - b)],
    byIds: (itemIds: number[]) => ['itemDescendants', 'by-ids', [...itemIds].sort((a, b) => a - b)],
    single: (itemId: number) => ['itemDescendants', 'single', itemId] as const,
  },
  itemFields: {
    all: () => ['itemFields'] as const,
  },
  itemStatuses: {
    all: () => ['itemStatuses'],
    detail: (itemStatusId: number) => [
      ...commonQueryKeys.itemStatuses.all(),
      'detail',
      itemStatusId,
    ],
  },
  spmCustomFields: {
    all: () => ['spmCustomFields'],
  },
  loggedWorkSummary: (itemId: number) => ['loggedWorkSummary', itemId],
  dependencies: {
    byItemId: (itemId: number) => ['dependencies', 'byItemId', itemId] as const,
  },
  itemsTable: {
    visibleItems: 'itemsTable.visibleItems',
    visibleProgress: 'itemsTable.visibleProgress',
    visibleSchedules: 'itemsTable.visibleSchedules',
    visibleDependencies: 'itemsTable.visibleDependencies',
    visibleUsers: 'itemsTable.visibleUsers',
    visibleItemFinancials: 'itemsTable.visibleItemFinancials',
  },
};
